/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { jsx } from '@emotion/react'
import { type FunctionComponent } from 'react'

import { FormattedMessage } from '@modules/i18n'
import useCreateBudget, {
  type CreateBudgetBody,
  defaultCreateBudgetValues,
} from '@modules/billing-lib/mutations/useCreateBudget'
import Feature from '@modules/utils/feature'
import useOrganizationId from '@modules/billing-lib/hooks/useOrganizationId'

import { isFeatureActivated } from '@/store'

import BudgetSettingsR1 from '../components/BudgetSettingsR1'
import BudgetSettings from '../components/BudgetSettings'
import useBillingNotificationsNav from '../../hooks/useBillingNotificationsNav'

import type { BudgetConfigurationValues } from '../types'

const CreateBudget: FunctionComponent = () => {
  const { goToBillingNotifications } = useBillingNotificationsNav()
  const organizationId = useOrganizationId()

  const createBudgetMutation = useCreateBudget()

  const onCreateBudget = (values: BudgetConfigurationValues) => {
    if (!organizationId) {
      return
    }

    const newBudgetConfig: CreateBudgetBody = isBillingCostAlertsCloudResourceFeatureFlagEnabled
      ? {
          ...defaultCreateBudgetValues,
          scope_type: values.scopeType!,
          scope_values: values.scopeValues!,
          amount: values.amount,
          name: values.name,
        }
      : {
          // TODO: remove once create budget R1 is deprecated
          ...defaultCreateBudgetValues,
          scope_values: [organizationId],
          ...values,
        }

    createBudgetMutation.mutate(
      {
        organizationId,
        body: newBudgetConfig,
      },
      {
        onSuccess: goToBillingNotifications,
      },
    )
  }

  const isBillingCostAlertsCloudResourceFeatureFlagEnabled = isFeatureActivated(
    Feature.billingCostAlertsCloudResource,
  )

  const isSubmitting = createBudgetMutation.isLoading

  return isBillingCostAlertsCloudResourceFeatureFlagEnabled ? (
    <BudgetSettings
      alerts={defaultCreateBudgetValues.alerts}
      data-testid='billing.notifications.create-budget.budget-settings'
      isSubmitting={isSubmitting}
      isMutationError={createBudgetMutation.isError}
      onSubmit={onCreateBudget}
      title={
        <FormattedMessage
          id='billing.notifications.budgets.create-budget.title'
          defaultMessage='Create Budget'
        />
      }
    />
  ) : (
    <BudgetSettingsR1
      alerts={defaultCreateBudgetValues.alerts}
      data-testid='billing.notifications.create-budget.budget-settings-r1'
      isSubmitting={isSubmitting}
      isMutationError={createBudgetMutation.isError}
      onSubmit={onCreateBudget}
      title={
        <FormattedMessage
          id='billing.notifications.budgets.create-budget.title'
          defaultMessage='Create Budget'
        />
      }
    />
  )
}

export default CreateBudget
